import React, { useContext, useEffect, useState } from 'react'
import { AuthContext } from '../../context/auth/auth-context'
import { Servers } from '../../utils/api/servers'
import useSWR from 'swr'
import { axiosFetcher } from '../../utils/api/axios-fetcher'
import useWebSocket, { ReadyState } from 'react-use-websocket'
import { type WebSocketLike } from 'react-use-websocket/dist/lib/types'
import AutoSizer from 'react-virtualized-auto-sizer'
import { FixedSizeList as List } from 'react-window'
import { useLocation } from 'react-router-dom'
import useSound from 'use-sound'
import { v4 as uuid_v4 } from 'uuid'
import { Button, Card, CardContent, Stack, Tooltip, Typography } from '@mui/material'
import PowerOffIcon from '@mui/icons-material/PowerOff'
import tradeSfx from '../../assets/audio/trade-sfx.mp3'
import { useTradeStore } from '../../hooks/store/use-trade-store'
import { type StrategyTrade } from '../../models/trade/strategy-trade'
import Loader from '../../components/loader/loader'
import TradeCard from '../../components/card/trade/trade-card'

interface TradeCardListProps {
  strategyId: string | null
  tradeUrl: string | null
  height?: string
}

interface Message {
  channelId: string
  data: StrategyTrade
}

const TradeCardList: React.FC<TradeCardListProps> = ({ strategyId, tradeUrl, height = '100%' }) => {
  const tradeStore = useTradeStore()

  const { isAuthorized, accessToken } = useContext(AuthContext)
  useEffect(() => {
    if (isAuthorized) {
      console.debug('[Trades] Ready to connect')
      setShouldConnect(true)
    }
  }, [isAuthorized])

  const { data, error } = useSWR<StrategyTrade[], boolean>(
    isAuthorized ? [Servers.STRATEGY_BASE, strategyId, 'oms-management/lastTrades', accessToken, null] : null,
    axiosFetcher,
    {
      shouldRetryOnError: false,
    },
  )

  useEffect(() => {
    if (tradeUrl == null) {
      console.debug('[Trades] Skipped fetching last trades (reason: null url)')
      setAttemptedApiConnect(true)
      return
    }

    if (error === true || data == null) {
      // Do nothing
    } else {
      const dataReversed = data.reverse()
      tradeStore.addTrades(tradeUrl, dataReversed)
    }
  }, [tradeUrl, data, error])

  useEffect(() => {
    if (tradeUrl == null) {
      console.debug('[Trades] Skipped connecting to data stream (reason: null url)')
      setAttemptedApiConnect(true)
      return
    }
    if (tradeStore.trades[tradeUrl]?.length > 0) {
      console.debug('[Trades] Skipped fetching last trades (reason: trades present in store)')
      return
    }

    window.onbeforeunload = function (event) {
      event.preventDefault()
      console.debug('[Trades] Clearing store')
      tradeStore.clearStore(tradeUrl)
    }

    return () => {
      window.onbeforeunload = null
    }
  }, [tradeUrl])

  const [attemptedApiConnect, setAttemptedApiConnect] = useState<boolean>(false)
  const [reconnectAttemptNo, setReconnectAttemptNo] = useState<number>(0)
  const [maxAttemptedReconnects, setMaxAttemptedReconnects] = useState<boolean>(false)
  const [shouldConnect, setShouldConnect] = useState<boolean>(false)

  const { lastJsonMessage, getWebSocket, readyState }: {
    lastJsonMessage: Message | null
    getWebSocket: () => (WebSocketLike | null)
    readyState: number
  } = useWebSocket(
    tradeUrl,
    {
      share: true,
      protocols: ['Authorization', accessToken ?? 'no-token'],
      onOpen: () => {
        console.log('[Trades] WebSocket connection established')
        setAttemptedApiConnect(true)
        setReconnectAttemptNo(0)
      },
      onClose: (event) => {
        console.log('[Trades] WebSocket connection closed', event)
        setAttemptedApiConnect(true)
      },
      onError: (error) => {
        console.error('[Trades] Error encountered when processing message: ', error)
      },
      onReconnectStop: () => {
        console.error('[Books] Max reconnection attempts reached')
        setMaxAttemptedReconnects(true)
        setShouldConnect(false)
      },
      filter: (message) => {
        const parsedData = JSON.parse(message.data)
        return parsedData?.channelId === 'trade'
      },
      shouldReconnect: () => {
        const newAttemptCount = reconnectAttemptNo + 1
        setReconnectAttemptNo(newAttemptCount)
        return true
      },
      retryOnError: true,
    },
    shouldConnect,
  )

  const location = useLocation()
  useEffect(() => {
    if (getWebSocket()?.readyState === WebSocket.OPEN) {
      console.debug('[Trades] WebSocket connection already established')
      setAttemptedApiConnect(true)
    }
  }, [getWebSocket()?.readyState, location])

  const [play] = useSound(tradeSfx, { volume: 0.20, interrupt: true })
  useEffect(() => {
    if (tradeUrl == null) {
      return
    }

    if (lastJsonMessage != null) {
      tradeStore.addTrade(tradeUrl, lastJsonMessage.data)
      play()
    }
  }, [lastJsonMessage])

  return (
    <Card
      variant='outlined'
      sx={{
        width: 1,
        minHeight: '300px',
        height: readyState === ReadyState.OPEN ? height : '100%',
        padding: 1,
        background: 'transparent',
        borderRadius: 0,
      }}
    >
      <CardContent
        sx={{
          height: {
            xs: 'calc(100% - 24px)',
            lg: 'calc(100dvh - 48px - 80px - 32px)',
          },
          padding: 0,
          margin: 0,
        }}
      >
        <Tooltip title={
          <Typography variant='caption' whiteSpace={'pre-line'}>
            {
             `Green BG (Quote)
             Black BG (Hedge)
             Black Solid Border (Make)
             Grey Solid Border (Take)
             Grey Dashed Border (Missed, Size = 0)
             Size < 0 means partial unfilled (where size = remainder)`
            }
          </Typography>
        } placement='bottom' sx={{ whiteSpace: 'pre-line' }}>
          <Typography variant='h6' fontWeight='bold' marginBottom={1}>
            Trades
          </Typography>
        </Tooltip>

        {
          readyState === ReadyState.OPEN
            ? <AutoSizer>
              {
                ({ height, width }) =>
                  (
                    <List
                      width={width}
                      height={height}
                      itemSize={88}
                      itemData={tradeStore.trades[tradeUrl as string]}
                      itemCount={tradeStore.trades[tradeUrl as string]?.length ?? 0}
                    >
                      {
                        ({ data, index, style }) =>
                          <TradeCard
                            style={style}
                            data={data[index]}
                            key={uuid_v4()}
                          />
                      }
                    </List>
                  )
              }
            </AutoSizer>
            : <Stack alignItems='center' justifyContent='center' gap={1}>
              {
                attemptedApiConnect
                  ? <PowerOffIcon color='error' fontSize='large'/>
                  : <Loader/>
              }

              {
                (!maxAttemptedReconnects && reconnectAttemptNo > 0)
                  ? <Typography color='primary' align='center'>
                    {`Attempted reconnection ${reconnectAttemptNo} time${reconnectAttemptNo > 1 ? 's' : ''}`}
                  </Typography>
                  : null
              }

              {
                maxAttemptedReconnects
                  ? <>
                    <Typography color='primary' align='center'>
                      Max auto-reconnection attempts reached
                    </Typography>
                    <Button
                      variant='outlined'
                      size='small'
                      onClick={() => {
                        setReconnectAttemptNo(0)
                        setMaxAttemptedReconnects(false)
                        setShouldConnect(true)
                      }}
                    >
                      Reconnect
                    </Button>
                  </>
                  : null
              }
            </Stack>
        }
      </CardContent>
    </Card>
  )
}

export default TradeCardList
